/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { PageBreadcrumb, type PageBreadcrumbItem } from '../page-breadcrumb/page-breadcrumb'

export type PageHeaderBreadcrumbItem = PageBreadcrumbItem

export type PageHeaderProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
  preTitle?: ReactNode
  highlightTitle?: ReactNode
  postTitle?: ReactNode
  subtitle?: ReactNode
  breadcrumbItems?: PageBreadcrumbItem[]
  rightChild?: ReactNode
}

export function PageHeader({
  preTitle,
  highlightTitle,
  postTitle,
  subtitle,
  breadcrumbItems,
  rightChild,
  className,
  ...rest
}: PageHeaderProps) {
  const crumbs = breadcrumbItems?.length ? breadcrumbItems : null
  const hasTitlePart = preTitle != null && preTitle !== false && preTitle !== ''
  const hasHighlight = highlightTitle != null && highlightTitle !== false && highlightTitle !== ''
  const hasPost = postTitle != null && postTitle !== false && postTitle !== ''
  const showHeading = hasTitlePart || hasHighlight || hasPost
  const showSubtitle = subtitle != null && subtitle !== false
  const showMain = showHeading || showSubtitle || rightChild != null

  return (
    <div className={cn('flex w-full flex-col', className)} {...rest}>
      {crumbs ? <PageBreadcrumb className={cn('w-full min-w-0', showMain && 'mb-2')} items={crumbs} /> : null}

      {showMain ? (
        <div className="flex w-full min-w-0 flex-wrap items-stretch justify-between gap-4">
          <div className="min-w-0 flex-1">
            {showHeading ? (
              <h1 className="inline-flex flex-wrap items-baseline gap-2 font-bold text-3xl leading-tight md:text-5xl">
                {hasTitlePart ? <span className="text-foreground">{preTitle}</span> : null}
                {hasHighlight ? <span className="text-primary">{highlightTitle}</span> : null}
                {hasPost ? <span className="text-foreground">{postTitle}</span> : null}
              </h1>
            ) : null}

            {showSubtitle ? (
              <h2 className="mt-2 line-clamp-3 min-h-[50px] font-normal text-muted-foreground md:mt-3 md:text-md">
                {subtitle}
              </h2>
            ) : null}
          </div>

          {rightChild != null ? <div className="max-w-full shrink-0">{rightChild}</div> : null}
        </div>
      ) : null}
    </div>
  )
}
